import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, CoinIcon, EmptyState, OrderQrCard } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { MessageKey } from "@ec2/i18n";
import type { OrderView } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { generalError, type LocalizedError } from "../lib/errors";
import { orderUrl } from "../lib/links";
import { formatClock } from "../lib/time";
import { useActive } from "../state/hooks";
import { useSession } from "../state/session";
import { Shell } from "../components/Shell";
import { StyledQr } from "../components/StyledQr";
import { ErrorLine, LoadingLine, ScreenHeader } from "../components/common";

const STATUS_KEY: Record<OrderView["status"], MessageKey> = {
  open: "ec2.order.status.open",
  processed: "ec2.order.status.processed",
  cancelled: "ec2.order.status.cancelled",
  expired: "ec2.order.status.expired",
};

function useOrders() {
  const trpc = useTRPC();
  const { active } = useActive();
  return useQuery({
    ...trpc.shop.orders.queryOptions({ eventId: active?.eventId ?? "" }),
    enabled: Boolean(active),
    refetchInterval: 15_000,
  });
}

/** Order list: status + total; open orders lead to the QR ticket. */
export function OrdersScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { locale } = useSession();
  const orders = useOrders();
  const rows = orders.data ?? [];

  return (
    <Shell active="shop">
      <ScreenHeader title={t("ec2.order.title")} />
      <div className="flex flex-col gap-3 px-4" data-testid="ec-orders">
        {orders.isLoading && <LoadingLine />}
        {orders.data && rows.length === 0 && (
          <EmptyState title={t("ec2.order.empty.title")} body={t("ec2.order.empty.body")} accentClassName="text-bucks" />
        )}
        {rows.map((order) => (
          <button
            key={order.id}
            type="button"
            onClick={() => void navigate({ to: "/orders/$orderId", params: { orderId: order.id } })}
            data-testid="ec-order-row"
            data-status={order.status}
            className="flex items-center gap-3 rounded-(--radius-card) border border-border bg-surface px-4 py-3 text-left focus-visible:outline-2 focus-visible:outline-primary"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-fg">
                {order.items.map((item) => `${item.quantity}× ${item.name}`).join(", ")}
              </p>
              <p className="text-[13px] font-medium text-fg-secondary">
                {t(STATUS_KEY[order.status])} · #{order.orderNumber}
                {order.status === "open" && order.expiresAt
                  ? ` · ${t("ec2.order.expiresAt", { time: formatClock(order.expiresAt, locale) })}`
                  : ""}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 font-display text-lg font-[750] text-bucks tabular-nums">
              <CoinIcon size={14} aria-hidden />
              {new Intl.NumberFormat(locale).format(order.totalBucks)}
            </span>
          </button>
        ))}
      </div>
    </Shell>
  );
}

/** Receipt-ticket screen: order QR + code + number, cancel while open. */
export function OrderDetailScreen({ orderId }: { orderId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { me } = useActive();
  const orders = useOrders();
  const cancel = useMutation(trpc.shop.cancelOrder.mutationOptions());
  const [error, setError] = useState<LocalizedError | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const order = orders.data?.find((row) => row.id === orderId);

  const doCancel = async () => {
    setError(null);
    try {
      await cancel.mutateAsync({ orderId });
      setCancelled(true);
      await queryClient.invalidateQueries();
      void me.refetch();
    } catch (err) {
      setError(generalError(err));
    }
  };

  return (
    <Shell active="shop">
      <ScreenHeader title={t("ec2.order.title")} />
      <div className="flex flex-col gap-4 px-4 pb-6" data-testid="ec-order-detail">
        {orders.isLoading && <LoadingLine />}
        {!orders.isLoading && !order && <ErrorLine error={{ key: "ec2.error.notFound" }} />}
        {order && (
          <>
            <OrderQrCard
              qr={<StyledQr value={orderUrl(order.code)} size={168} eyes="ink" />}
              itemName={order.items.map((item) => `${item.quantity}× ${item.name}`).join(", ")}
              code={order.code}
              codeLabel={t("ec2.order.code")}
              caption={t("ec2.order.showAtBooth")}
              orderNumber={String(order.orderNumber)}
            />
            <p className="text-center text-[13px] font-medium text-fg-secondary">{t(STATUS_KEY[order.status])}</p>
            {cancelled && (
              <p role="status" className="text-center text-[13px] font-medium text-success">
                {t("ec2.order.cancelled")}
              </p>
            )}
            <ErrorLine error={error} className="text-center" />
            {order.status === "open" && !cancelled && (
              <Button variant="ghost" onClick={() => void doCancel()} disabled={cancel.isPending} data-testid="ec-order-cancel">
                {t("ec2.order.cancel")}
              </Button>
            )}
            {(order.status !== "open" || cancelled) && (
              <Button variant="primary" onClick={() => void navigate({ to: "/shop" })}>
                {t("ec2.shop.title")}
              </Button>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}
